import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { surveyQuestionsApi } from '../surveyApi';
import type { SurveyQuestion } from '../surveyTypes';
import { sampleSurveyQuestion } from '../utils/sampleSurveyQuestion';

/**
 * Create or edit one survey question.
 *
 * Two halves, and the fieldset is what separates them: what the question ASKS, and what the
 * answer form draws when somebody answers it. The second half is the reason the questions are
 * rows at all — each box replaces a branch that would otherwise be written against a question's
 * identity.
 *
 * No position field: where a question sits in the questionnaire is assigned by the server and
 * changed by moving the row, not by typing a number here — a rewording is not a reorder.
 */
export function SurveyQuestionModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: SurveyQuestion | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [number, setNumber] = useState(editing?.number ?? '');
  const [text, setText] = useState(editing?.text ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [guidance, setGuidance] = useState(editing?.guidance ?? '');
  const [collectsDetails, setCollectsDetails] = useState(editing?.collectsDetails ?? true);
  const [collectsDate, setCollectsDate] = useState(editing?.collectsDate ?? false);
  const [collectsCaseStatus, setCollectsCaseStatus] = useState(editing?.collectsCaseStatus ?? false);
  const [collectsIdNumber, setCollectsIdNumber] = useState(editing?.collectsIdNumber ?? false);
  const [collectsCountry, setCollectsCountry] = useState(editing?.collectsCountry ?? false);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = number.trim().length > 0 && text.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      number: number.trim(),
      text: text.trim(),
      description: description.trim() || null,
      guidance: guidance.trim() || null,
      collectsDetails,
      collectsDate,
      collectsCaseStatus,
      collectsIdNumber,
      collectsCountry,
    };

    try {
      if (editing) await surveyQuestionsApi.update(editing.id, request);
      else await surveyQuestionsApi.create(request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this question.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'survey-question' });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleSurveyQuestion();

    /*
      The item number is generated on a CREATE and left alone on an EDIT. On an edit it is the
      one field the reader did not choose — CS Form 212 did — and "never a field the reader has
      already decided" is what keeps a generator from fighting the form instead of filling it.
    */
    if (!editing) setNumber(sample.number);

    setText(sample.text);
    setDescription(sample.description);
    setGuidance(sample.guidance);
    setCollectsDetails(sample.collectsDetails);
    setCollectsDate(sample.collectsDate);
    setCollectsCaseStatus(sample.collectsCaseStatus);
    setCollectsIdNumber(sample.collectsIdNumber);
    setCollectsCountry(sample.collectsCountry);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      // Names the item on an edit, so a reader who opened the wrong row sees it before they type.
      title={editing ? `Edit item ${editing.number}` : 'New question'}
      size="2xl"
      id="survey-question-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'survey-question-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="survey-question-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            id="survey-question-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="survey-question-number"
          label="Item number"
          required
          description="What the question is known by — 34a, 35b, 40c. Unique among the questions being asked."
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          error={number.trim() ? error?.fieldError('number') : 'Enter an item number.'}
          disabled={saving}
        />

        <TextField
          id="survey-question-text"
          label="Question"
          required
          description="The whole sentence, as the employee is asked it. Where CS Form 212 prints the question, its wording settles it."
          value={text}
          onChange={(e) => setText(e.target.value)}
          error={text.trim() ? error?.fieldError('text') : 'Enter the question.'}
          disabled={saving}
        />

        <TextField
          id="survey-question-description"
          label="Description"
          description="One line saying what this question is for. Shown on this list, not on the answer form."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={error?.fieldError('description')}
          disabled={saving}
        />

        <TextField
          id="survey-question-guidance"
          label="Form guidance"
          description="A note shown under the answer form when this question is being answered. Item 34a carries the meaning of consanguinity."
          value={guidance}
          onChange={(e) => setGuidance(e.target.value)}
          error={error?.fieldError('guidance')}
          disabled={saving}
        />

        {/*
          A real fieldset rather than a heading over a div: these five belong together and a
          screen reader should announce the group's name with each of them. They are what makes
          this a catalog rather than a list of sentences.
        */}
        <FormFieldset legend="What the answer form asks for on a Yes">
          <label className="flex items-start gap-2.5" htmlFor="survey-question-collects-details">
            <Checkbox
              id="survey-question-collects-details"
              checked={collectsDetails}
              onChange={(e) => setCollectsDetails(e.target.checked)}
              disabled={saving}
              className="mt-0.5"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm text-slate-700">Details</span>
              <span className="text-xs text-slate-500">
                The PDS's "If YES, give details" and its "specify". Required once the answer is
                Yes, because that is what the form asks for.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2.5" htmlFor="survey-question-collects-date">
            <Checkbox
              id="survey-question-collects-date"
              checked={collectsDate}
              onChange={(e) => setCollectsDate(e.target.checked)}
              disabled={saving}
              className="mt-0.5"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm text-slate-700">Date filed</span>
              <span className="text-xs text-slate-500">
                Item 35b asks when a criminal case was filed. Optional even on a Yes — a date the
                employee cannot recall is a real answer.
              </span>
            </span>
          </label>

          <label
            className="flex items-start gap-2.5"
            htmlFor="survey-question-collects-case-status"
          >
            <Checkbox
              id="survey-question-collects-case-status"
              checked={collectsCaseStatus}
              onChange={(e) => setCollectsCaseStatus(e.target.checked)}
              disabled={saving}
              className="mt-0.5"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm text-slate-700">Status of case</span>
              <span className="text-xs text-slate-500">
                Item 35b asks where the case stands now. Optional even on a Yes.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2.5" htmlFor="survey-question-collects-id-number">
            <Checkbox
              id="survey-question-collects-id-number"
              checked={collectsIdNumber}
              onChange={(e) => setCollectsIdNumber(e.target.checked)}
              disabled={saving}
              className="mt-0.5"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm text-slate-700">ID number</span>
              <span className="text-xs text-slate-500">
                Items 40b and 40c ask for the number on the card. Optional even on a Yes — a card
                applied for and not yet issued is a real answer.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2.5" htmlFor="survey-question-collects-country">
            <Checkbox
              id="survey-question-collects-country"
              checked={collectsCountry}
              onChange={(e) => setCollectsCountry(e.target.checked)}
              disabled={saving}
              className="mt-0.5"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm text-slate-700">Country</span>
              <span className="text-xs text-slate-500">
                Item 39 asks which country. Chosen from the country catalog, never typed — the
                form asks for a country and a nationality is a demonym. Required once the answer
                is Yes.
              </span>
            </span>
          </label>
        </FormFieldset>

        {/*
          Said on the form rather than on the tick: these flags change what every future answer
          to this question is asked, which is a change to what the module collects.
        */}
        <p className="text-xs text-slate-500">
          This question is asked on the survey of every personnel record, and the wording here is
          what they read — correcting it corrects every answer already filed under it. Deleting a
          question removes it from the form and leaves the answers already given alone. Turning a
          follow-up off is refused while answers still carry one, because the next correction of
          each of those would clear it.
        </p>
      </div>
    </Modal>
  );
}
